import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import {
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
  Line,
  ComposedChart,
} from "recharts";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.obscurium.pro";

const getAuthToken = (): string | null => {
  try {
    const stored = sessionStorage.getItem("OBSC_JWT");
    if (stored) return stored;
  } catch {}
  return process.env.REACT_APP_API_KEY || null;
};

// Colors
const COLORS = {
  primary: "#00e5ff",
  success: "#00ff88",
  warning: "#ffa500",
  danger: "#ff4757",
  purple: "#8338ec",
  pink: "#ff006e",
  blue: "#3a86ff",
  yellow: "#ffbe0b",
  teal: "#00b894",
  orange: "#fd9644",
};

const CHAIN_COLORS: Record<string, string> = {
  ETHEREUM: "#627EEA",
  BSC: "#F3BA2F",
  POLYGON: "#8247E5",
  ARBITRUM: "#28A0F0",
  OPTIMISM: "#FF0420",
  AVALANCHE: "#E84142",
  BASE: "#0052FF",
  SOLANA: "#9945FF",
  FANTOM: "#1969FF",
  LINEA: "#61DFFF",
};

const STATUS_COLORS: Record<string, string> = {
  completed: COLORS.success,
  failed: COLORS.danger,
  initiated: COLORS.primary,
  processing: COLORS.warning,
  securing: COLORS.yellow,
  converting: COLORS.purple,
  dispatch: COLORS.blue,
};

const PIE_COLORS = [COLORS.primary, COLORS.success, COLORS.purple, COLORS.warning, COLORS.pink, COLORS.blue, COLORS.teal, COLORS.orange, COLORS.yellow, COLORS.danger];

// Styled Components
const Container = styled.div`
  width: 100%;
  max-width: 1800px;
  margin: 0 auto;
  padding: 1.5rem;
  min-height: 100vh;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid rgba(0, 229, 255, 0.1);
  padding-bottom: 0.5rem;
  flex-wrap: wrap;
`;

const Tab = styled.button<{ $active?: boolean }>`
  background: ${(props) =>
    props.$active ? "rgba(0, 229, 255, 0.15)" : "transparent"};
  border: 1px solid
    ${(props) => (props.$active ? "rgba(0, 229, 255, 0.3)" : "transparent")};
  border-radius: 8px;
  padding: 0.6rem 1.25rem;
  color: ${(props) => (props.$active ? "#00e5ff" : "#7a8fa5")};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #00e5ff;
    background: rgba(0, 229, 255, 0.08);
  }
`;

const Grid = styled.div<{ $cols?: number }>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.$cols || 4}, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 1400px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 1000px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div<{ $span?: number }>`
  background: rgba(20, 25, 45, 0.75);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(0, 229, 255, 0.12);
  border-radius: 12px;
  padding: 1.25rem;
  grid-column: ${(props) => (props.$span ? `span ${props.$span}` : "span 1")};

  @media (max-width: 1000px) {
    grid-column: span 1;
  }
`;

const CardTitle = styled.h3`
  font-size: 0.75rem;
  font-weight: 600;
  color: #7a8fa5;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 0.75rem 0;
`;

const KPIValue = styled.div<{ $color?: string; $size?: string }>`
  font-size: ${(props) => props.$size || "2rem"};
  font-weight: 700;
  color: ${(props) => props.$color || "#00e5ff"};
  line-height: 1.2;
`;

const KPISubtext = styled.div`
  font-size: 0.75rem;
  color: #7a8fa5;
  margin-top: 0.25rem;
`;

const GrowthIndicator = styled.span<{ $positive?: boolean }>`
  display: inline-flex;
  align-items: center;
  font-size: 0.7rem;
  color: ${(props) => (props.$positive ? COLORS.success : COLORS.danger)};
  margin-left: 0.5rem;
`;

const ChartContainer = styled.div`
  height: 280px;
  margin-top: 0.5rem;
`;

const SmallChartContainer = styled.div`
  height: 200px;
`;


const Button = styled.button<{ $variant?: "primary" | "secondary" | "danger" }>`
  background: ${(props) => {
    switch (props.$variant) {
      case "secondary":
        return "rgba(0, 0, 0, 0.3)";
      case "danger":
        return "linear-gradient(45deg, #ff4757, #ff3838)";
      default:
        return "linear-gradient(45deg, #00e5ff, #1e4a5f)";
    }
  }};
  border: 1px solid
    ${(props) =>
      props.$variant === "secondary" ? "rgba(0, 229, 255, 0.15)" : "transparent"};
  border-radius: 8px;
  padding: 0.6rem 1.25rem;
  color: #ffffff;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 229, 255, 0.2);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const Select = styled.select`
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(0, 229, 255, 0.15);
  border-radius: 8px;
  padding: 0.6rem 1rem;
  color: #ffffff;
  font-size: 0.8rem;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #00e5ff;
  }

  option {
    background: #0a1419;
  }
`;

const TableContainer = styled.div`
  background: rgba(20, 25, 45, 0.75);
  border: 1px solid rgba(0, 229, 255, 0.12);
  border-radius: 12px;
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background: rgba(0, 0, 0, 0.3);
`;

const TableRow = styled.tr<{ $clickable?: boolean }>`
  border-bottom: 1px solid rgba(0, 229, 255, 0.08);
  transition: background 0.2s ease;
  ${(props) =>
    props.$clickable &&
    `cursor: pointer; &:hover { background: rgba(0, 229, 255, 0.05); }`}
`;

const TableHeader = styled.th`
  padding: 0.875rem 1rem;
  text-align: left;
  font-size: 0.7rem;
  font-weight: 600;
  color: #7a8fa5;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableCell = styled.td`
  padding: 0.875rem 1rem;
  font-size: 0.8rem;
  color: #ffffff;
`;

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  background: ${(props) => `${STATUS_COLORS[props.$status] || COLORS.primary}20`};
  color: ${(props) => STATUS_COLORS[props.$status] || COLORS.primary};
`;

const ChainBadge = styled.span<{ $chain: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 600;
  background: ${(props) => `${CHAIN_COLORS[props.$chain] || COLORS.primary}20`};
  color: ${(props) => CHAIN_COLORS[props.$chain] || COLORS.primary};
`;

const Address = styled.span`
  font-family: monospace;
  font-size: 0.75rem;
  color: #00e5ff;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.875rem 1rem;
  background: rgba(0, 0, 0, 0.2);
  border-top: 1px solid rgba(0, 229, 255, 0.08);
  font-size: 0.8rem;
  color: #7a8fa5;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: rgba(20, 25, 45, 0.98);
  border: 1px solid rgba(0, 229, 255, 0.2);
  border-radius: 16px;
  padding: 1.5rem;
  max-width: 700px;
  width: 100%;
  max-height: 85vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(0, 229, 255, 0.1);
`;

const ModalTitle = styled.h2`
  font-size: 1.1rem;
  color: #ffffff;
  margin: 0;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #7a8fa5;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  &:hover {
    color: #ffffff;
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const DetailItem = styled.div`
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  padding: 0.75rem;
`;

const DetailLabel = styled.div`
  font-size: 0.65rem;
  color: #7a8fa5;
  text-transform: uppercase;
  margin-bottom: 0.25rem;
`;

const DetailValue = styled.div`
  font-size: 0.8rem;
  color: #ffffff;
  word-break: break-all;
  font-family: monospace;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.25rem;
  flex-wrap: wrap;
`;

const RecentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 250px;
  overflow-y: auto;
`;

const RecentItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.6rem;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  font-size: 0.75rem;
`;

const ChainPairTable = styled.div`
  max-height: 220px;
  overflow-y: auto;
`;

const LoginContainer = styled.div`
  max-width: 380px;
  margin: 3rem auto;
  padding: 2rem;
  background: rgba(20, 25, 45, 0.75);
  border: 1px solid rgba(0, 229, 255, 0.15);
  border-radius: 16px;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: 1px solid rgba(0, 229, 255, 0.15);
  background: rgba(0, 0, 0, 0.3);
  color: #fff;
  font-size: 0.875rem;
  margin-bottom: 0.75rem;

  &:focus {
    outline: none;
    border-color: #00e5ff;
  }
`;

const ErrorText = styled.div`
  color: #ff4757;
  font-size: 0.8rem;
  margin-bottom: 0.75rem;
`;

const SectionTitle = styled.h2`
  font-size: 1rem;
  font-weight: 600;
  color: #ffffff;
  margin: 2rem 0 1rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(0, 229, 255, 0.1);
`;

const MetricRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(0, 229, 255, 0.05);
  font-size: 0.8rem;

  &:last-child {
    border-bottom: none;
  }
`;

const MetricLabel = styled.span`
  color: #7a8fa5;
`;

const MetricValue = styled.span<{ $color?: string }>`
  color: ${(props) => props.$color || "#ffffff"};
  font-weight: 600;
`;

const HealthIndicator = styled.div<{ $status: "good" | "warning" | "critical" }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.3rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${(props) => {
    switch (props.$status) {
      case "good": return "rgba(0, 255, 136, 0.15)";
      case "warning": return "rgba(255, 165, 0, 0.15)";
      case "critical": return "rgba(255, 71, 87, 0.15)";
    }
  }};
  color: ${(props) => {
    switch (props.$status) {
      case "good": return COLORS.success;
      case "warning": return COLORS.warning;
      case "critical": return COLORS.danger;
    }
  }};

  &::before {
    content: "";
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: currentColor;
  }
`;

const ProgressBar = styled.div<{ $value: number; $color?: string }>`
  height: 6px;
  background: rgba(0, 229, 255, 0.1);
  border-radius: 3px;
  overflow: hidden;
  margin-top: 0.5rem;

  &::after {
    content: "";
    display: block;
    height: 100%;
    width: ${(props) => Math.min(props.$value, 100)}%;
    background: ${(props) => props.$color || COLORS.primary};
    border-radius: 3px;
    transition: width 0.3s ease;
  }
`;

// Interfaces
interface DashboardStats {
  totalTransfers: number;
  completedTransfers: number;
  failedTransfers: number;
  activeTransfers: number;
  successRate: number;
  totalVolume: number;
  avgCompletionTime: number;
  transfersToday: number;
  transfersThisWeek: number;
  transfersThisMonth: number;
  volumeToday: number;
  volumeThisWeek: number;
  volumeThisMonth: number;
  byStatus: Array<{ status: string; count: number }>;
  byOriginChain: Array<{ chain: string; count: number }>;
  byDestinationChain: Array<{ chain: string; count: number }>;
  chainPairs: Array<{ origin_chain: string; destination_chain: string; count: number }>;
  hourlyVolume: Array<{ time: string; count: number; volume: number }>;
  dailyVolume: Array<{ time: string; count: number; volume: number }>;
  avgCompletionTimeByChain: Array<{ chain: string; avg_time: number }>;
  successRateByChain: Array<{ chain: string; total: number; completed: number; failed: number; success_rate: number }>;
  topFailureReasons: Array<{ reason: string; count: number }>;
  failuresByChain: Array<{ chain: string; count: number }>;
  recentTransfers: Array<{ id: string; origin_chain: string; destination_chain: string; amount: string; status: string; created_at: string; sender_address: string }>;
  uniqueAddresses: number;
  // New fields
  transfersYesterday: number;
  transfersLastWeek: number;
  transfersLastMonth: number;
  growthRateDaily: number;
  growthRateWeekly: number;
  growthRateMonthly: number;
  uniqueAddressesToday: number;
  uniqueAddressesThisWeek: number;
  uniqueAddressesThisMonth: number;
  returningUsers: number;
  newUsersToday: number;
  avgTransfersPerUser: number;
  avgTransferAmount: number;
  medianTransferAmount: number;
  maxTransferAmount: number;
  minTransferAmount: number;
  volumeYesterday: number;
  sameChainTransfers: number;
  crossChainTransfers: number;
  sameChainVolume: number;
  crossChainVolume: number;
  avgProcessingTimeByStatus: Array<{ status: string; avg_duration: number }>;
  transfersInEachStatus: Array<{ status: string; count: number; avg_duration: number }>;
  pendingDuration: Array<{ status: string; avg_duration: number }>;
  peakHour: number;
  peakHourCount: number;
  peakDayOfWeek: string;
  peakDayCount: number;
  hourlyDistribution: Array<{ hour: number; count: number }>;
  dayOfWeekDistribution: Array<{ day: string; count: number }>;
  volumeByOriginChain: Array<{ chain: string; volume: number }>;
  volumeByDestinationChain: Array<{ chain: string; volume: number }>;
  transfersWithRetries: number;
  avgRetryCount: number;
  maxRetryCount: number;
  retrySuccessRate: number;
  recentFailures: Array<{ id: string; origin_chain: string; dest_chain: string; amount: string; failure_reason: string; sender_address: string; created_at: string; failed_at: string; attempt_count: number }>;
  completionTimeP50: number;
  completionTimeP90: number;
  completionTimeP99: number;
  topUsersByVolume: Array<{ address: string; volume: number; count: number }>;
  topUsersByCount: Array<{ address: string; volume: number; count: number }>;
  failureRateLast1h: number;
  failureRateLast24h: number;
  avgCompletionLast1h: number;
  activeTransfersAge: Array<{ range: string; count: number }>;
}

interface Transaction {
  id: string;
  type: string;
  origin_chain: string;
  destination_chain: string;
  amount: string;
  receiving_address: string;
  input_address: string;
  sender_address: string | null;
  status: string;
  failure_reason?: string | null;
  user_deposit_tx_hash: string | null;
  final_tx_hash?: string | null;
  created_at: string;
  updated_at: string;
}

interface TransferDetails {
  id: string;
  status: string;
  originChain: string;
  destinationChain: string;
  amount: string;
  inputWalletAddress: string;
  inputWalletBalance: string;
  receivingAddress: string;
  senderAddress: string | null;
  userDepositTxHash: string | null;
  binanceDepositTxHash: string | null;
  binanceWithdrawId: string | null;
  finalTxHash: string | null;
  failureReason: string | null;
  attemptCount: number;
  createdAt: string;
  updatedAt: string;
}

// Helpers
const truncateAddress = (address: string | null | undefined) => {
  if (!address) return "N/A";
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

const formatDate = (date: string | Date) => new Date(date).toLocaleString();
const formatNumber = (num: number) => num.toLocaleString();
const formatDuration = (seconds: number) => {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${Math.round(seconds / 3600)}h ${Math.round((seconds % 3600) / 60)}m`;
};

const formatVolume = (vol: number) => {
  if (vol >= 1000) return `${(vol / 1000).toFixed(2)}K`;
  return vol.toFixed(4);
};

const getHealthStatus = (failureRate: number): "good" | "warning" | "critical" => {
  if (failureRate < 5) return "good";
  if (failureRate < 15) return "warning";
  return "critical";
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ background: "rgba(20, 25, 45, 0.95)", padding: "0.5rem 0.75rem", borderRadius: "8px", border: "1px solid rgba(0, 229, 255, 0.2)" }}>
        <div style={{ color: "#7a8fa5", fontSize: "0.7rem" }}>{label}</div>
        {payload.map((p: any, i: number) => (
          <div key={i} style={{ color: p.color, fontSize: "0.8rem", fontWeight: 600 }}>
            {p.name}: {typeof p.value === "number" ? p.value.toLocaleString() : p.value}
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export const AdminPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"overview" | "analytics" | "users" | "health" | "transactions" | "recovery">("overview");
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => !!getAuthToken());
  const [statusFilter, setStatusFilter] = useState("");
  const [limit, setLimit] = useState(25);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [selectedTransfer, setSelectedTransfer] = useState<TransferDetails | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState("");

  const login = async () => {
    setError("");
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      if (response.ok && data.status === "success") {
        sessionStorage.setItem("OBSC_JWT", data.data.token);
        setIsAuthenticated(true);
        setEmail("");
        setPassword("");
      } else {
        setError(data.message || "Login failed");
      }
    } catch {
      setError("Network error");
    }
  };

  const fetchDashboardStats = useCallback(async () => {
    try {
      const token = getAuthToken();
      if (!token) return;
      const response = await fetch(`${API_BASE_URL}/v1/admin/stats/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok && data.status === "success") {
        setDashboardStats(data.data);
      }
    } catch (err) {
      console.error("Failed to fetch dashboard stats:", err);
    }
  }, []);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      if (!token) {
        setLoading(false);
        return;
      }
      const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() });
      if (statusFilter) params.append("status", statusFilter);

      const response = await fetch(`${API_BASE_URL}/v1/admin/all-transactions?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok && data.status === "success") {
        setTransactions(data.data.transactions);
        setTotal(data.data.total);
      }
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [limit, offset, statusFilter]);

  const fetchTransferDetails = async (transferId: string) => {
    try {
      const token = getAuthToken();
      if (!token) return;
      const response = await fetch(`${API_BASE_URL}/v1/admin/recovery/transfer/${transferId}/details`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok && data.status === "success") {
        setSelectedTransfer(data.data);
      }
    } catch (err) {
      console.error("Failed to fetch transfer details:", err);
    }
  };

  const handleContinueFlow = async (transferId: string) => {
    setActionLoading(true);
    setActionMessage("");
    try {
      const token = getAuthToken();
      if (!token) return;
      const response = await fetch(`${API_BASE_URL}/v1/admin/recovery/transfer/${transferId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setActionMessage(response.ok ? "Recovery initiated" : data.message || "Failed");
      if (response.ok) {
        fetchTransactions();
        fetchDashboardStats();
      }
    } catch {
      setActionMessage("Network error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReturnFunds = async (transferId: string) => {
    const address = prompt("Enter address to return funds to (leave empty for sender):");
    setActionLoading(true);
    setActionMessage("");
    try {
      const token = getAuthToken();
      if (!token) return;
      const response = await fetch(`${API_BASE_URL}/v1/admin/recovery/return-funds/${transferId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ to_address: address || "" }),
      });
      const data = await response.json();
      setActionMessage(response.ok ? `Funds returned to ${data.data?.returnAddress}` : data.message || "Failed");
      if (response.ok) {
        fetchTransactions();
        fetchDashboardStats();
      }
    } catch {
      setActionMessage("Network error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleForceComplete = async (transferId: string) => {
    if (!window.confirm("Force complete this transfer?")) return;
    setActionLoading(true);
    setActionMessage("");
    try {
      const token = getAuthToken();
      if (!token) return;
      const response = await fetch(`${API_BASE_URL}/v1/admin/recovery/force-complete/${transferId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setActionMessage(response.ok ? "Transfer completed" : data.message || "Failed");
      if (response.ok) {
        fetchTransactions();
        fetchDashboardStats();
        fetchTransferDetails(transferId);
      }
    } catch {
      setActionMessage("Network error");
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchDashboardStats();
      fetchTransactions();
    }
  }, [isAuthenticated, fetchDashboardStats, fetchTransactions]);

  useEffect(() => {
    if (isAuthenticated && activeTab === "transactions") {
      fetchTransactions();
    }
  }, [activeTab, statusFilter, limit, offset, isAuthenticated, fetchTransactions]);

  if (!isAuthenticated) {
    return (
      <Container>
        <LoginContainer>
          <Title style={{ marginBottom: "1.5rem", textAlign: "center" }}>Admin Login</Title>
          <Input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <Input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
          {error && <ErrorText>{error}</ErrorText>}
          <Button style={{ width: "100%" }} onClick={login}>Login</Button>
        </LoginContainer>
      </Container>
    );
  }

  const stats = dashboardStats;

  // Prepare chart data with safe defaults for all arrays
  const sortedDailyVolume = stats?.dailyVolume?.length ? [...stats.dailyVolume].sort((a, b) => a.time.localeCompare(b.time)) : [];
  const hourlyChartData = stats?.hourlyDistribution?.length ? [...stats.hourlyDistribution].sort((a, b) => a.hour - b.hour).map(h => ({ hour: `${h.hour}:00`, count: h.count })) : [];
  const dayOrderMap: Record<string, number> = { Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6 };
  const dayOfWeekData = stats?.dayOfWeekDistribution?.length ? [...stats.dayOfWeekDistribution].sort((a, b) => dayOrderMap[a.day] - dayOrderMap[b.day]) : [];

  // Safe chart data helpers
  const safeArray = <T,>(arr: T[] | null | undefined): T[] => arr || [];
  const successRateByChainData = safeArray(stats?.successRateByChain);
  const avgCompletionTimeByChainData = safeArray(stats?.avgCompletionTimeByChain);
  const volumeByOriginChainData = safeArray(stats?.volumeByOriginChain);
  const volumeByDestinationChainData = safeArray(stats?.volumeByDestinationChain);
  const byStatusData = safeArray(stats?.byStatus);
  const activeTransfersAgeData = safeArray(stats?.activeTransfersAge);
  const failuresByChainData = safeArray(stats?.failuresByChain);

  return (
    <Container>
      <Header>
        <Title>Admin Dashboard</Title>
        <HeaderActions>
          <Button $variant="secondary" onClick={() => { fetchDashboardStats(); fetchTransactions(); }}>
            Refresh
          </Button>
          <Button $variant="secondary" onClick={() => { sessionStorage.removeItem("OBSC_JWT"); setIsAuthenticated(false); }}>
            Logout
          </Button>
        </HeaderActions>
      </Header>

      <TabContainer>
        <Tab $active={activeTab === "overview"} onClick={() => setActiveTab("overview")}>Overview</Tab>
        <Tab $active={activeTab === "analytics"} onClick={() => setActiveTab("analytics")}>Analytics</Tab>
        <Tab $active={activeTab === "users"} onClick={() => setActiveTab("users")}>Users</Tab>
        <Tab $active={activeTab === "health"} onClick={() => setActiveTab("health")}>System Health</Tab>
        <Tab $active={activeTab === "transactions"} onClick={() => setActiveTab("transactions")}>Transactions</Tab>
        <Tab $active={activeTab === "recovery"} onClick={() => setActiveTab("recovery")}>Recovery</Tab>
      </TabContainer>

      {/* ==================== OVERVIEW TAB ==================== */}
      {activeTab === "overview" && stats && (
        <>
          {/* Main KPIs */}
          <Grid $cols={5}>
            <Card>
              <CardTitle>Total Transfers</CardTitle>
              <KPIValue>{formatNumber(stats.totalTransfers)}</KPIValue>
              <KPISubtext>{formatNumber(stats.uniqueAddresses)} unique users</KPISubtext>
            </Card>
            <Card>
              <CardTitle>Success Rate</CardTitle>
              <KPIValue $color={stats.successRate >= 80 ? COLORS.success : stats.successRate >= 50 ? COLORS.warning : COLORS.danger}>
                {stats.successRate.toFixed(1)}%
              </KPIValue>
              <KPISubtext>{formatNumber(stats.completedTransfers)} / {formatNumber(stats.completedTransfers + stats.failedTransfers)}</KPISubtext>
            </Card>
            <Card>
              <CardTitle>Active Now</CardTitle>
              <KPIValue $color={COLORS.warning}>{formatNumber(stats.activeTransfers)}</KPIValue>
              <KPISubtext>In progress</KPISubtext>
            </Card>
            <Card>
              <CardTitle>Total Volume</CardTitle>
              <KPIValue $size="1.6rem">{formatVolume(stats.totalVolume)}</KPIValue>
              <KPISubtext>All time</KPISubtext>
            </Card>
            <Card>
              <CardTitle>Avg Completion</CardTitle>
              <KPIValue $size="1.6rem">{formatDuration(stats.avgCompletionTime)}</KPIValue>
              <KPISubtext>P50: {formatDuration(stats.completionTimeP50)}</KPISubtext>
            </Card>
          </Grid>

          {/* Time-based metrics with growth */}
          <Grid $cols={4}>
            <Card>
              <CardTitle>Today</CardTitle>
              <KPIValue $size="1.8rem">{formatNumber(stats.transfersToday)}</KPIValue>
              <GrowthIndicator $positive={stats.growthRateDaily >= 0}>
                {stats.growthRateDaily >= 0 ? "↑" : "↓"} {Math.abs(stats.growthRateDaily).toFixed(1)}%
              </GrowthIndicator>
              <KPISubtext>vs yesterday ({stats.transfersYesterday})</KPISubtext>
            </Card>
            <Card>
              <CardTitle>This Week</CardTitle>
              <KPIValue $size="1.8rem">{formatNumber(stats.transfersThisWeek)}</KPIValue>
              <GrowthIndicator $positive={stats.growthRateWeekly >= 0}>
                {stats.growthRateWeekly >= 0 ? "↑" : "↓"} {Math.abs(stats.growthRateWeekly).toFixed(1)}%
              </GrowthIndicator>
              <KPISubtext>vs last week ({stats.transfersLastWeek})</KPISubtext>
            </Card>
            <Card>
              <CardTitle>This Month</CardTitle>
              <KPIValue $size="1.8rem">{formatNumber(stats.transfersThisMonth)}</KPIValue>
              <GrowthIndicator $positive={stats.growthRateMonthly >= 0}>
                {stats.growthRateMonthly >= 0 ? "↑" : "↓"} {Math.abs(stats.growthRateMonthly).toFixed(1)}%
              </GrowthIndicator>
              <KPISubtext>vs last month</KPISubtext>
            </Card>
            <Card>
              <CardTitle>Volume Today</CardTitle>
              <KPIValue $size="1.8rem">{formatVolume(stats.volumeToday)}</KPIValue>
              <KPISubtext>Yesterday: {formatVolume(stats.volumeYesterday)}</KPISubtext>
            </Card>
          </Grid>

          {/* Volume Chart */}
          <Grid $cols={1}>
            <Card>
              <CardTitle>Transfer Volume (Last 30 Days)</CardTitle>
              <ChartContainer>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={sortedDailyVolume}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0, 229, 255, 0.1)" />
                    <XAxis dataKey="time" tick={{ fill: "#7a8fa5", fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                    <YAxis yAxisId="left" tick={{ fill: "#7a8fa5", fontSize: 10 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fill: "#7a8fa5", fontSize: 10 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar yAxisId="left" dataKey="count" name="Transfers" fill={COLORS.primary} opacity={0.7} />
                    <Line yAxisId="right" type="monotone" dataKey="volume" name="Volume" stroke={COLORS.success} strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </ChartContainer>
            </Card>
          </Grid>

          {/* Chain Distribution */}
          <Grid $cols={3}>
            <Card>
              <CardTitle>By Origin Chain</CardTitle>
              <SmallChartContainer>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={stats.byOriginChain}
                      dataKey="count"
                      nameKey="chain"
                      cx="50%"
                      cy="50%"
                      outerRadius={65}
                      label={({ name, percent }) => `${name || ''} ${((percent || 0) * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {stats.byOriginChain.map((entry, index) => (
                        <Cell key={index} fill={CHAIN_COLORS[entry.chain] || PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </SmallChartContainer>
            </Card>

            <Card>
              <CardTitle>By Destination Chain</CardTitle>
              <SmallChartContainer>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={stats.byDestinationChain}
                      dataKey="count"
                      nameKey="chain"
                      cx="50%"
                      cy="50%"
                      outerRadius={65}
                      label={({ name, percent }) => `${name || ''} ${((percent || 0) * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {stats.byDestinationChain.map((entry, index) => (
                        <Cell key={index} fill={CHAIN_COLORS[entry.chain] || PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </SmallChartContainer>
            </Card>

            <Card>
              <CardTitle>Transfer Types</CardTitle>
              <SmallChartContainer>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={[
                        { name: "Cross-chain", value: stats.crossChainTransfers },
                        { name: "Same-chain", value: stats.sameChainTransfers },
                      ]}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={65}
                      label={({ name, percent }) => `${name || ''} ${((percent || 0) * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      <Cell fill={COLORS.purple} />
                      <Cell fill={COLORS.teal} />
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </SmallChartContainer>
            </Card>
          </Grid>

          {/* Popular Routes & Recent */}
          <Grid $cols={2}>
            <Card>
              <CardTitle>Popular Routes</CardTitle>
              <ChainPairTable>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeader>From</TableHeader>
                      <TableHeader>To</TableHeader>
                      <TableHeader>Count</TableHeader>
                    </TableRow>
                  </TableHead>
                  <tbody>
                    {(stats.chainPairs || []).slice(0, 8).map((cp, i) => (
                      <TableRow key={i}>
                        <TableCell><ChainBadge $chain={cp.origin_chain}>{cp.origin_chain}</ChainBadge></TableCell>
                        <TableCell><ChainBadge $chain={cp.destination_chain}>{cp.destination_chain}</ChainBadge></TableCell>
                        <TableCell>{cp.count}</TableCell>
                      </TableRow>
                    ))}
                  </tbody>
                </Table>
              </ChainPairTable>
            </Card>

            <Card>
              <CardTitle>Recent Transfers</CardTitle>
              <RecentList>
                {(stats.recentTransfers || []).slice(0, 8).map((t) => (
                  <RecentItem key={t.id} style={{ cursor: "pointer" }} onClick={() => fetchTransferDetails(t.id)}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      <ChainBadge $chain={t.origin_chain}>{t.origin_chain.slice(0, 3)}</ChainBadge>
                      <span style={{ color: "#7a8fa5" }}>→</span>
                      <ChainBadge $chain={t.destination_chain}>{t.destination_chain.slice(0, 3)}</ChainBadge>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      <span>{parseFloat(t.amount).toFixed(4)}</span>
                      <StatusBadge $status={t.status}>{t.status.slice(0, 4)}</StatusBadge>
                    </div>
                  </RecentItem>
                ))}
              </RecentList>
            </Card>
          </Grid>
        </>
      )}

      {/* ==================== ANALYTICS TAB ==================== */}
      {activeTab === "analytics" && stats && (
        <>
          {/* Volume Statistics */}
          <SectionTitle>Volume Statistics</SectionTitle>
          <Grid $cols={5}>
            <Card>
              <CardTitle>Avg Transfer</CardTitle>
              <KPIValue $size="1.5rem">{(stats.avgTransferAmount || 0).toFixed(4)}</KPIValue>
            </Card>
            <Card>
              <CardTitle>Median Transfer</CardTitle>
              <KPIValue $size="1.5rem">{(stats.medianTransferAmount || 0).toFixed(4)}</KPIValue>
            </Card>
            <Card>
              <CardTitle>Min Transfer</CardTitle>
              <KPIValue $size="1.5rem">{(stats.minTransferAmount || 0).toFixed(4)}</KPIValue>
            </Card>
            <Card>
              <CardTitle>Max Transfer</CardTitle>
              <KPIValue $size="1.5rem">{(stats.maxTransferAmount || 0).toFixed(4)}</KPIValue>
            </Card>
            <Card>
              <CardTitle>Cross-chain Vol</CardTitle>
              <KPIValue $size="1.5rem">{formatVolume(stats.crossChainVolume || 0)}</KPIValue>
              <KPISubtext>Same-chain: {formatVolume(stats.sameChainVolume || 0)}</KPISubtext>
            </Card>
          </Grid>

          {/* Timing Analysis */}
          <SectionTitle>Peak Analysis</SectionTitle>
          <Grid $cols={2}>
            <Card>
              <CardTitle>Hourly Distribution</CardTitle>
              <SmallChartContainer>
                {hourlyChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={hourlyChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0, 229, 255, 0.1)" />
                      <XAxis dataKey="hour" tick={{ fill: "#7a8fa5", fontSize: 9 }} interval={2} />
                      <YAxis tick={{ fill: "#7a8fa5", fontSize: 10 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="count" name="Transfers" fill={COLORS.primary} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#7a8fa5" }}>No data</div>
                )}
              </SmallChartContainer>
              <KPISubtext style={{ marginTop: "0.5rem" }}>Peak hour: {stats.peakHour || 0}:00 ({stats.peakHourCount || 0} transfers)</KPISubtext>
            </Card>

            <Card>
              <CardTitle>Day of Week Distribution</CardTitle>
              <SmallChartContainer>
                {dayOfWeekData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dayOfWeekData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0, 229, 255, 0.1)" />
                      <XAxis dataKey="day" tick={{ fill: "#7a8fa5", fontSize: 9 }} tickFormatter={(v) => v ? v.slice(0, 3) : ''} />
                      <YAxis tick={{ fill: "#7a8fa5", fontSize: 10 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="count" name="Transfers" fill={COLORS.purple} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#7a8fa5" }}>No data</div>
                )}
              </SmallChartContainer>
              <KPISubtext style={{ marginTop: "0.5rem" }}>Peak day: {stats.peakDayOfWeek || 'N/A'} ({stats.peakDayCount || 0} transfers)</KPISubtext>
            </Card>
          </Grid>

          {/* Performance Metrics */}
          <SectionTitle>Performance Metrics</SectionTitle>
          <Grid $cols={3}>
            <Card>
              <CardTitle>Completion Time Percentiles</CardTitle>
              <MetricRow>
                <MetricLabel>P50 (Median)</MetricLabel>
                <MetricValue>{formatDuration(stats.completionTimeP50)}</MetricValue>
              </MetricRow>
              <MetricRow>
                <MetricLabel>P90</MetricLabel>
                <MetricValue>{formatDuration(stats.completionTimeP90)}</MetricValue>
              </MetricRow>
              <MetricRow>
                <MetricLabel>P99</MetricLabel>
                <MetricValue>{formatDuration(stats.completionTimeP99)}</MetricValue>
              </MetricRow>
              <MetricRow>
                <MetricLabel>Average</MetricLabel>
                <MetricValue>{formatDuration(stats.avgCompletionTime)}</MetricValue>
              </MetricRow>
            </Card>

            <Card>
              <CardTitle>Success Rate by Chain</CardTitle>
              <SmallChartContainer>
                {successRateByChainData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={successRateByChainData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0, 229, 255, 0.1)" />
                      <XAxis dataKey="chain" tick={{ fill: "#7a8fa5", fontSize: 9 }} />
                      <YAxis tick={{ fill: "#7a8fa5", fontSize: 10 }} domain={[0, 100]} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="success_rate" name="Success %" fill={COLORS.success} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#7a8fa5" }}>No data</div>
                )}
              </SmallChartContainer>
            </Card>

            <Card>
              <CardTitle>Avg Completion by Chain</CardTitle>
              <SmallChartContainer>
                {avgCompletionTimeByChainData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={avgCompletionTimeByChainData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0, 229, 255, 0.1)" />
                      <XAxis type="number" tick={{ fill: "#7a8fa5", fontSize: 10 }} tickFormatter={(v) => `${Math.round(v/60)}m`} />
                      <YAxis type="category" dataKey="chain" tick={{ fill: "#7a8fa5", fontSize: 9 }} width={70} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="avg_time" name="Avg Time (s)" fill={COLORS.blue} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#7a8fa5" }}>No data</div>
                )}
              </SmallChartContainer>
            </Card>
          </Grid>

          {/* Volume by Chain */}
          <SectionTitle>Volume by Chain</SectionTitle>
          <Grid $cols={2}>
            <Card>
              <CardTitle>Outbound Volume (by Origin)</CardTitle>
              <SmallChartContainer>
                {volumeByOriginChainData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={volumeByOriginChainData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0, 229, 255, 0.1)" />
                      <XAxis dataKey="chain" tick={{ fill: "#7a8fa5", fontSize: 9 }} />
                      <YAxis tick={{ fill: "#7a8fa5", fontSize: 10 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="volume" name="Volume">
                        {volumeByOriginChainData.map((entry, index) => (
                          <Cell key={index} fill={CHAIN_COLORS[entry.chain] || PIE_COLORS[index % PIE_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#7a8fa5" }}>No data</div>
                )}
              </SmallChartContainer>
            </Card>

            <Card>
              <CardTitle>Inbound Volume (by Destination)</CardTitle>
              <SmallChartContainer>
                {volumeByDestinationChainData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={volumeByDestinationChainData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0, 229, 255, 0.1)" />
                      <XAxis dataKey="chain" tick={{ fill: "#7a8fa5", fontSize: 9 }} />
                      <YAxis tick={{ fill: "#7a8fa5", fontSize: 10 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="volume" name="Volume">
                        {volumeByDestinationChainData.map((entry, index) => (
                          <Cell key={index} fill={CHAIN_COLORS[entry.chain] || PIE_COLORS[index % PIE_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#7a8fa5" }}>No data</div>
                )}
              </SmallChartContainer>
            </Card>
          </Grid>

          {/* Status Breakdown */}
          <SectionTitle>Status Breakdown</SectionTitle>
          <Grid $cols={2}>
            <Card>
              <CardTitle>By Status</CardTitle>
              <SmallChartContainer>
                {byStatusData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={byStatusData}
                        dataKey="count"
                        nameKey="status"
                        cx="50%"
                        cy="50%"
                        outerRadius={70}
                        label={({ name, percent }) => `${name || ''} ${((percent || 0) * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {byStatusData.map((entry, index) => (
                          <Cell key={index} fill={STATUS_COLORS[entry.status] || PIE_COLORS[index % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#7a8fa5" }}>No data</div>
                )}
              </SmallChartContainer>
            </Card>

            <Card>
              <CardTitle>Active Transfer Duration</CardTitle>
              <RecentList>
                {(stats.transfersInEachStatus || []).map((s, i) => (
                  <RecentItem key={i}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      <StatusBadge $status={s.status}>{s.status}</StatusBadge>
                      <span style={{ color: "#7a8fa5" }}>{s.count} transfers</span>
                    </div>
                    <span>Avg: {formatDuration(s.avg_duration)}</span>
                  </RecentItem>
                ))}
              </RecentList>
            </Card>
          </Grid>
        </>
      )}

      {/* ==================== USERS TAB ==================== */}
      {activeTab === "users" && stats && (
        <>
          {/* User Metrics */}
          <SectionTitle>User Engagement</SectionTitle>
          <Grid $cols={5}>
            <Card>
              <CardTitle>Total Users</CardTitle>
              <KPIValue>{formatNumber(stats.uniqueAddresses)}</KPIValue>
            </Card>
            <Card>
              <CardTitle>Users Today</CardTitle>
              <KPIValue>{formatNumber(stats.uniqueAddressesToday)}</KPIValue>
            </Card>
            <Card>
              <CardTitle>Users This Week</CardTitle>
              <KPIValue>{formatNumber(stats.uniqueAddressesThisWeek)}</KPIValue>
            </Card>
            <Card>
              <CardTitle>New Users Today</CardTitle>
              <KPIValue $color={COLORS.success}>{formatNumber(stats.newUsersToday)}</KPIValue>
            </Card>
            <Card>
              <CardTitle>Returning Users</CardTitle>
              <KPIValue $color={COLORS.purple}>{formatNumber(stats.returningUsers)}</KPIValue>
            </Card>
          </Grid>

          <Grid $cols={2}>
            <Card>
              <CardTitle>Avg Transfers Per User</CardTitle>
              <KPIValue $size="2.5rem">{(stats.avgTransfersPerUser || 0).toFixed(2)}</KPIValue>
              <ProgressBar $value={((stats.avgTransfersPerUser || 0) / 10) * 100} />
            </Card>
            <Card>
              <CardTitle>User Retention</CardTitle>
              <KPIValue $size="2.5rem" $color={COLORS.success}>
                {stats.uniqueAddresses > 0 ? ((stats.returningUsers / stats.uniqueAddresses) * 100).toFixed(1) : 0}%
              </KPIValue>
              <KPISubtext>Users with 2+ transfers</KPISubtext>
            </Card>
          </Grid>

          {/* Top Users */}
          <Grid $cols={2}>
            <Card>
              <CardTitle>Top Users by Volume</CardTitle>
              <RecentList>
                {(stats.topUsersByVolume || []).slice(0, 10).map((u, i) => (
                  <RecentItem key={i}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      <span style={{ color: COLORS.primary, fontWeight: 600 }}>#{i + 1}</span>
                      <Address>{u.address}</Address>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ color: COLORS.success, fontWeight: 600 }}>{u.volume.toFixed(4)}</div>
                      <div style={{ color: "#7a8fa5", fontSize: "0.65rem" }}>{u.count} txs</div>
                    </div>
                  </RecentItem>
                ))}
              </RecentList>
            </Card>

            <Card>
              <CardTitle>Top Users by Transfers</CardTitle>
              <RecentList>
                {(stats.topUsersByCount || []).slice(0, 10).map((u, i) => (
                  <RecentItem key={i}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      <span style={{ color: COLORS.primary, fontWeight: 600 }}>#{i + 1}</span>
                      <Address>{u.address}</Address>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ color: COLORS.purple, fontWeight: 600 }}>{u.count} txs</div>
                      <div style={{ color: "#7a8fa5", fontSize: "0.65rem" }}>{u.volume.toFixed(4)} vol</div>
                    </div>
                  </RecentItem>
                ))}
              </RecentList>
            </Card>
          </Grid>
        </>
      )}

      {/* ==================== SYSTEM HEALTH TAB ==================== */}
      {activeTab === "health" && stats && (
        <>
          <SectionTitle>System Health</SectionTitle>
          <Grid $cols={4}>
            <Card>
              <CardTitle>Overall Status</CardTitle>
              <div style={{ marginTop: "0.5rem" }}>
                <HealthIndicator $status={getHealthStatus(stats.failureRateLast24h)}>
                  {getHealthStatus(stats.failureRateLast24h) === "good" ? "Healthy" : getHealthStatus(stats.failureRateLast24h) === "warning" ? "Warning" : "Critical"}
                </HealthIndicator>
              </div>
            </Card>
            <Card>
              <CardTitle>Failure Rate (1h)</CardTitle>
              <KPIValue $size="1.8rem" $color={stats.failureRateLast1h < 5 ? COLORS.success : stats.failureRateLast1h < 15 ? COLORS.warning : COLORS.danger}>
                {stats.failureRateLast1h.toFixed(1)}%
              </KPIValue>
            </Card>
            <Card>
              <CardTitle>Failure Rate (24h)</CardTitle>
              <KPIValue $size="1.8rem" $color={stats.failureRateLast24h < 5 ? COLORS.success : stats.failureRateLast24h < 15 ? COLORS.warning : COLORS.danger}>
                {stats.failureRateLast24h.toFixed(1)}%
              </KPIValue>
            </Card>
            <Card>
              <CardTitle>Avg Completion (1h)</CardTitle>
              <KPIValue $size="1.8rem">{formatDuration(stats.avgCompletionLast1h)}</KPIValue>
            </Card>
          </Grid>

          {/* Retry Analysis */}
          <SectionTitle>Retry Analysis</SectionTitle>
          <Grid $cols={4}>
            <Card>
              <CardTitle>Transfers with Retries</CardTitle>
              <KPIValue>{formatNumber(stats.transfersWithRetries)}</KPIValue>
            </Card>
            <Card>
              <CardTitle>Avg Retry Count</CardTitle>
              <KPIValue>{(stats.avgRetryCount || 0).toFixed(2)}</KPIValue>
            </Card>
            <Card>
              <CardTitle>Max Retries</CardTitle>
              <KPIValue>{stats.maxRetryCount || 0}</KPIValue>
            </Card>
            <Card>
              <CardTitle>Retry Success Rate</CardTitle>
              <KPIValue $color={COLORS.success}>{(stats.retrySuccessRate || 0).toFixed(1)}%</KPIValue>
            </Card>
          </Grid>

          {/* Active Transfers Age */}
          <Grid $cols={2}>
            <Card>
              <CardTitle>Active Transfers Age Distribution</CardTitle>
              <SmallChartContainer>
                {activeTransfersAgeData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={activeTransfersAgeData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0, 229, 255, 0.1)" />
                      <XAxis type="number" tick={{ fill: "#7a8fa5", fontSize: 10 }} />
                      <YAxis type="category" dataKey="range" tick={{ fill: "#7a8fa5", fontSize: 9 }} width={80} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="count" name="Transfers" fill={COLORS.warning} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#7a8fa5" }}>No active transfers</div>
                )}
              </SmallChartContainer>
            </Card>

            <Card>
              <CardTitle>Failures by Chain</CardTitle>
              <SmallChartContainer>
                {failuresByChainData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={failuresByChainData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0, 229, 255, 0.1)" />
                      <XAxis dataKey="chain" tick={{ fill: "#7a8fa5", fontSize: 9 }} />
                      <YAxis tick={{ fill: "#7a8fa5", fontSize: 10 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="count" name="Failures" fill={COLORS.danger} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#7a8fa5" }}>No failures</div>
                )}
              </SmallChartContainer>
            </Card>
          </Grid>

          {/* Failure Analysis */}
          <SectionTitle>Failure Analysis</SectionTitle>
          <Grid $cols={2}>
            <Card>
              <CardTitle>Top Failure Reasons</CardTitle>
              <RecentList>
                {(stats.topFailureReasons || []).slice(0, 8).map((r, i) => (
                  <RecentItem key={i}>
                    <span style={{ color: "#ff4757", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {r.reason || "Unknown"}
                    </span>
                    <StatusBadge $status="failed">{r.count}</StatusBadge>
                  </RecentItem>
                ))}
              </RecentList>
            </Card>

            <Card>
              <CardTitle>Recent Failures</CardTitle>
              <RecentList>
                {(stats.recentFailures || []).slice(0, 6).map((f, i) => (
                  <RecentItem key={i} style={{ cursor: "pointer" }} onClick={() => fetchTransferDetails(f.id)}>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <ChainBadge $chain={f.origin_chain}>{f.origin_chain.slice(0, 3)}</ChainBadge>
                        <span style={{ color: "#7a8fa5" }}>→</span>
                        <ChainBadge $chain={f.dest_chain}>{f.dest_chain.slice(0, 3)}</ChainBadge>
                      </div>
                      <div style={{ color: "#ff4757", fontSize: "0.65rem", marginTop: "0.25rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "200px" }}>
                        {f.failure_reason}
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div>{parseFloat(f.amount).toFixed(4)}</div>
                      <div style={{ color: "#7a8fa5", fontSize: "0.65rem" }}>Attempts: {f.attempt_count}</div>
                    </div>
                  </RecentItem>
                ))}
              </RecentList>
            </Card>
          </Grid>
        </>
      )}

      {/* ==================== TRANSACTIONS TAB ==================== */}
      {activeTab === "transactions" && (
        <>
          <div style={{ display: "flex", gap: "0.75rem", marginBottom: "1rem", flexWrap: "wrap" }}>
            <Select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setOffset(0); }}>
              <option value="">All Statuses</option>
              <option value="initiated">Initiated</option>
              <option value="processing">Processing</option>
              <option value="securing">Securing</option>
              <option value="converting">Converting</option>
              <option value="dispatch">Dispatch</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </Select>
            <Select value={limit} onChange={(e) => { setLimit(parseInt(e.target.value)); setOffset(0); }}>
              <option value="25">25 per page</option>
              <option value="50">50 per page</option>
              <option value="100">100 per page</option>
            </Select>
          </div>

          <TableContainer>
            {loading ? (
              <div style={{ padding: "2rem", textAlign: "center", color: "#7a8fa5" }}>Loading...</div>
            ) : (
              <>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeader>ID</TableHeader>
                      <TableHeader>Route</TableHeader>
                      <TableHeader>Amount</TableHeader>
                      <TableHeader>Sender</TableHeader>
                      <TableHeader>Status</TableHeader>
                      <TableHeader>Created</TableHeader>
                    </TableRow>
                  </TableHead>
                  <tbody>
                    {transactions.map((tx) => (
                      <TableRow key={tx.id} $clickable onClick={() => fetchTransferDetails(tx.id)}>
                        <TableCell><Address>{truncateAddress(tx.id)}</Address></TableCell>
                        <TableCell>
                          <ChainBadge $chain={tx.origin_chain}>{tx.origin_chain}</ChainBadge>
                          <span style={{ margin: "0 0.3rem", color: "#7a8fa5" }}>→</span>
                          <ChainBadge $chain={tx.destination_chain}>{tx.destination_chain}</ChainBadge>
                        </TableCell>
                        <TableCell>{tx.amount}</TableCell>
                        <TableCell><Address>{truncateAddress(tx.sender_address)}</Address></TableCell>
                        <TableCell><StatusBadge $status={tx.status}>{tx.status}</StatusBadge></TableCell>
                        <TableCell style={{ fontSize: "0.7rem", color: "#7a8fa5" }}>{formatDate(tx.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </tbody>
                </Table>
                <Pagination>
                  <span>Showing {offset + 1} to {Math.min(offset + limit, total)} of {total}</span>
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <Button $variant="secondary" onClick={() => setOffset(Math.max(0, offset - limit))} disabled={offset === 0}>Prev</Button>
                    <Button $variant="secondary" onClick={() => setOffset(offset + limit)} disabled={offset + limit >= total}>Next</Button>
                  </div>
                </Pagination>
              </>
            )}
          </TableContainer>
        </>
      )}

      {/* ==================== RECOVERY TAB ==================== */}
      {activeTab === "recovery" && stats && (
        <>
          <Grid $cols={3}>
            <Card>
              <CardTitle>Active (Recoverable)</CardTitle>
              <KPIValue $color={COLORS.warning}>{formatNumber(stats.activeTransfers)}</KPIValue>
            </Card>
            <Card>
              <CardTitle>Failed</CardTitle>
              <KPIValue $color={COLORS.danger}>{formatNumber(stats.failedTransfers)}</KPIValue>
            </Card>
            <Card>
              <CardTitle>Failures by Chain</CardTitle>
              <RecentList>
                {(stats.failuresByChain || []).slice(0, 4).map((f, i) => (
                  <RecentItem key={i}>
                    <ChainBadge $chain={f.chain}>{f.chain}</ChainBadge>
                    <span style={{ color: "#ff4757" }}>{f.count}</span>
                  </RecentItem>
                ))}
              </RecentList>
            </Card>
          </Grid>

          <Card>
            <CardTitle>Recent Failed/Active Transfers</CardTitle>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>ID</TableHeader>
                    <TableHeader>Route</TableHeader>
                    <TableHeader>Amount</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Reason</TableHeader>
                    <TableHeader>Actions</TableHeader>
                  </TableRow>
                </TableHead>
                <tbody>
                  {transactions.filter(tx => tx.status !== "completed").slice(0, 15).map((tx) => (
                    <TableRow key={tx.id}>
                      <TableCell><Address>{truncateAddress(tx.id)}</Address></TableCell>
                      <TableCell>{tx.origin_chain} → {tx.destination_chain}</TableCell>
                      <TableCell>{tx.amount}</TableCell>
                      <TableCell><StatusBadge $status={tx.status}>{tx.status}</StatusBadge></TableCell>
                      <TableCell style={{ fontSize: "0.7rem", color: "#ff4757", maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {tx.failure_reason || "-"}
                      </TableCell>
                      <TableCell>
                        <Button $variant="secondary" style={{ padding: "0.3rem 0.5rem", fontSize: "0.7rem" }} onClick={() => fetchTransferDetails(tx.id)}>
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </TableContainer>
          </Card>
        </>
      )}

      {/* Transfer Details Modal */}
      {selectedTransfer && (
        <ModalOverlay onClick={() => { setSelectedTransfer(null); setActionMessage(""); }}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Transfer Details</ModalTitle>
              <CloseButton onClick={() => { setSelectedTransfer(null); setActionMessage(""); }}>&times;</CloseButton>
            </ModalHeader>

            <div style={{ marginBottom: "1rem" }}>
              <StatusBadge $status={selectedTransfer.status} style={{ fontSize: "0.8rem", padding: "0.3rem 0.8rem" }}>
                {selectedTransfer.status}
              </StatusBadge>
            </div>

            <DetailGrid>
              <DetailItem>
                <DetailLabel>ID</DetailLabel>
                <DetailValue>{selectedTransfer.id}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Route</DetailLabel>
                <DetailValue>{selectedTransfer.originChain} → {selectedTransfer.destinationChain}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Amount</DetailLabel>
                <DetailValue>{selectedTransfer.amount}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Attempts</DetailLabel>
                <DetailValue>{selectedTransfer.attemptCount}</DetailValue>
              </DetailItem>
              <DetailItem style={{ gridColumn: "span 2" }}>
                <DetailLabel>Input Wallet</DetailLabel>
                <DetailValue>{selectedTransfer.inputWalletAddress}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Input Balance</DetailLabel>
                <DetailValue>{selectedTransfer.inputWalletBalance || "N/A"}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Sender</DetailLabel>
                <DetailValue>{selectedTransfer.senderAddress || "N/A"}</DetailValue>
              </DetailItem>
              <DetailItem style={{ gridColumn: "span 2" }}>
                <DetailLabel>Receiving Address</DetailLabel>
                <DetailValue>{selectedTransfer.receivingAddress}</DetailValue>
              </DetailItem>
              {selectedTransfer.userDepositTxHash && (
                <DetailItem style={{ gridColumn: "span 2" }}>
                  <DetailLabel>User Deposit Tx</DetailLabel>
                  <DetailValue>{selectedTransfer.userDepositTxHash}</DetailValue>
                </DetailItem>
              )}
              {selectedTransfer.binanceDepositTxHash && (
                <DetailItem style={{ gridColumn: "span 2" }}>
                  <DetailLabel>Binance Deposit Tx</DetailLabel>
                  <DetailValue>{selectedTransfer.binanceDepositTxHash}</DetailValue>
                </DetailItem>
              )}
              {selectedTransfer.finalTxHash && (
                <DetailItem style={{ gridColumn: "span 2" }}>
                  <DetailLabel>Final Tx</DetailLabel>
                  <DetailValue>{selectedTransfer.finalTxHash}</DetailValue>
                </DetailItem>
              )}
              {selectedTransfer.failureReason && (
                <DetailItem style={{ gridColumn: "span 2" }}>
                  <DetailLabel>Failure Reason</DetailLabel>
                  <DetailValue style={{ color: "#ff4757" }}>{selectedTransfer.failureReason}</DetailValue>
                </DetailItem>
              )}
              <DetailItem>
                <DetailLabel>Created</DetailLabel>
                <DetailValue>{formatDate(selectedTransfer.createdAt)}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Updated</DetailLabel>
                <DetailValue>{formatDate(selectedTransfer.updatedAt)}</DetailValue>
              </DetailItem>
            </DetailGrid>

            {selectedTransfer.status !== "completed" && (
              <ActionButtons>
                {selectedTransfer.status !== "failed" && (
                  <Button onClick={() => handleContinueFlow(selectedTransfer.id)} disabled={actionLoading}>
                    {actionLoading ? "Processing..." : "Continue Flow"}
                  </Button>
                )}
                <Button $variant="secondary" onClick={() => handleReturnFunds(selectedTransfer.id)} disabled={actionLoading}>
                  Return Funds
                </Button>
                {(selectedTransfer.status === "converting" || selectedTransfer.status === "dispatch") && (
                  <Button $variant="danger" onClick={() => handleForceComplete(selectedTransfer.id)} disabled={actionLoading}>
                    Force Complete
                  </Button>
                )}
              </ActionButtons>
            )}

            {actionMessage && (
              <div style={{ marginTop: "1rem", padding: "0.75rem", borderRadius: "8px", fontSize: "0.8rem", textAlign: "center", background: actionMessage.includes("error") || actionMessage.includes("Failed") ? "rgba(255, 71, 87, 0.1)" : "rgba(0, 255, 136, 0.1)", color: actionMessage.includes("error") || actionMessage.includes("Failed") ? "#ff4757" : "#00ff88" }}>
                {actionMessage}
              </div>
            )}
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};
